import React, { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

import { setDownload, setLoading } from '../../../../store/actions/dashboardActions'

const waitForNextPaint = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  })

const waitForImages = async (root) => {
  const images = Array.from(root.querySelectorAll('img'))
  await Promise.all(
    images.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) {
            resolve()
            return
          }
          const done = () => {
            img.removeEventListener('load', done)
            img.removeEventListener('error', done)
            resolve()
          }
          img.addEventListener('load', done)
          img.addEventListener('error', done)
        })
    )
  )
}

const waitForCanvases = async (root, timeoutMs = 4000) => {
  const start = Date.now()
  while (Date.now() - start < timeoutMs) {
    const canvases = Array.from(root.querySelectorAll('canvas'))
    const ready = canvases.every((canvas) => canvas.width && canvas.height)
    if (ready) {
      return
    }
    await new Promise((resolve) => setTimeout(resolve, 100))
  }
}

const waitForLoaders = async (root, timeoutMs = 12000) => {
  const start = Date.now()
  while (Date.now() - start < timeoutMs) {
    const loaders = root.querySelectorAll(
      '[aria-label*="loading"], .blocks-wrapper'
    )
    if (!loaders.length) {
      return
    }
    await new Promise((resolve) => setTimeout(resolve, 150))
  }
}

const PDFExport = ({
  children,
  fileName = 'Dashboard_Charts.pdf',
  padding = 10,
  bottomSpace = 60,
  waitForMs = 300,
  downloadKey = 'pdf',
}) => {
  const dispatch = useDispatch()
  const { download, filters, footer, tableFilters } = useSelector(
    (state) => state.dashboard
  )
  const hasDownloadedRef = useRef(false)
  const pageRefs = useRef([])

  const pages = useMemo(() => {
    const items = React.Children.toArray(children)
    const chunked = []
    for (let i = 0; i < items.length; i += 1) {
      chunked.push(items.slice(i, i + 1))
    }
    return chunked
  }, [children])

  useEffect(() => {
    pageRefs.current = pageRefs.current.slice(0, pages.length)
  }, [pages.length])

  useEffect(() => {
    if (download !== downloadKey || !pages.length) {
      return
    }
    if (hasDownloadedRef.current) {
      return
    }
    hasDownloadedRef.current = true

    const exportPages = async () => {
      let pdf = null
      let pageWidth = 0
      let pageHeight = 0
      let exportHeight = 0

      for (let index = 0; index < pageRefs.current.length; index += 1) {
        const pageElement = pageRefs.current[index]
        if (!pageElement) {
          continue
        }

        await waitForNextPaint()
        await waitForImages(pageElement)
        await waitForLoaders(pageElement)
        await waitForCanvases(pageElement)
        if (waitForMs) {
          await new Promise((resolve) => setTimeout(resolve, waitForMs))
        }

        const canvas = await html2canvas(pageElement, {
          backgroundColor: '#ffffff',
          useCORS: true,
          allowTaint: true,
          scrollY: -window.scrollY,
          scale: window.devicePixelRatio || 1,
        })

        if (!pdf) {
          pageWidth = canvas.width
          pageHeight = canvas.height
          exportHeight = pageHeight + bottomSpace
          pdf = new jsPDF({ // eslint-disable-line new-cap
            orientation: pageWidth >= pageHeight ? 'landscape' : 'portrait',
            unit: 'px',
            format: [pageWidth, exportHeight],
          })
        } else {
          pdf.addPage(
            [pageWidth, exportHeight],
            pageWidth >= pageHeight ? 'landscape' : 'portrait'
          )
        }

        const imageData = canvas.toDataURL('image/jpeg', 1.0)
        pdf.addImage(imageData, 'jpeg', 0, 0, pageWidth, pageHeight)
      }

      if (pdf) {
        pdf.save(fileName)
      }
      dispatch(setDownload(''))
      dispatch(setLoading(false))
    }

    exportPages()
  }, [download, pages.length, fileName, padding, dispatch])

  const filterSummary = download === downloadKey && (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.5rem',
        padding: '0.5rem 0',
        fontSize: '0.75rem',
        borderBottom: '1px solid #e0e0e0',
      }}
    >
      <span>
        Start Date:{' '}
        {filters?.dates?.[0]
          ? new Date(filters.dates[0]).toLocaleDateString()
          : ''}
      </span>
      <span>
        End Date:{' '}
        {filters?.dates?.[1]
          ? new Date(filters.dates[1]).toLocaleDateString()
          : ''}
      </span>
      <span>Client: {footer?.company || 'All Companies'}</span>
      <span>Vacancy: {footer?.job || 'All Jobs'}</span>
      {footer?.recruiter_id ? (
        <span>Recruiter: {footer.recruiter_id}</span>
      ) : null}
      <span>Stage: {tableFilters?.stage || 'All'}</span>
      <span>Channel: {tableFilters?.channel || 'All'}</span>
    </div>
  )

  return (
    <div style={{ width: '100%' }}>
      {pages.map((pageItems, pageIndex) => (
        <div
          key={`report-page-${pageIndex}`}
          ref={(element) => {
            pageRefs.current[pageIndex] = element
          }}
          style={{
            display: 'grid',
            gridTemplateRows: 'minmax(0, 1fr)',
            gap: '1rem',
            padding: '1rem 1rem 2rem',
            marginBottom: '1.5rem',
            backgroundColor: '#ffffff',
          }}
        >
          {filterSummary}
          {pageItems.map((item, itemIndex) => (
            <div
              key={`report-item-${pageIndex}-${itemIndex}`}
              style={{
                minHeight: 0,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {item}
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

export default PDFExport
